import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, retry } from 'rxjs/operators';
import {
  WebserviceResponse,
  ProjectDeleteImpact,
  Projects,
  ProjectCrmCompareResponse,
  PositionsList,
  ProjectsComment,
  Positions,
  Jobs,
  Orders,
  CrmSyncRequest
} from '../interfaces';
import { AlertService, IAlertType } from "./alert.service";
import { restBaseUrl } from "../utils/rest-url";
import { normalizeSingletonResponse } from "../utils/response";

@Injectable({
  providedIn: 'root'
})

export class ProjectsStoreService {
  projects: Projects[] = [];

  private readonly endpoint = restBaseUrl();

  constructor(
    private http: HttpClient,
    private alertDispatcher: AlertService
  ) {
  }

  private handleError(error: HttpErrorResponse){
    let errorMessage = 'Unknown error!';
    if (error.error instanceof ErrorEvent) {
      // Client-side errors
      errorMessage = `Client-side error: ${error.error.message}`;
    } else {
      // Server-side errors
      errorMessage = `Server-side error code: ${error.status} - message: ${error.message}`;
    }

    this.alertDispatcher.show({
      message: errorMessage,
      clrAlertType: IAlertType.DANGER
    }, 5000);

    // return throwError(errorMessage); deprecated
    return throwError(() => new Error(errorMessage));
  }

  private mapProjectFromRms(item: any): Projects {
    const rawProjectNumber = String(item?.project_number ?? '');
    const projectNumberPadded = /^\d+$/.test(rawProjectNumber)
      ? rawProjectNumber.padStart(4, '0')
      : rawProjectNumber;

    return {
      project_id: item?.project_id,
      budget: Number(item?.budget ?? 0),
      cid: Number(item?.company_id ?? 0),
      cid_project_id: `${item?.company_id ?? 0}.${projectNumberPadded}`,
      comment: item?.comment ?? '',
      comment_phm: item?.comment_phm ?? '',
      company_name: item?.company_name ?? '',
      cosmos_id: item?.cosmos_id ?? '',
      description: item?.description ?? '',
      employee_leader: item?.employee_leader ?? undefined,
      employee_phm: item?.employee_phm ?? undefined,
      end_project: item?.end_date ?? '',
      id_project: projectNumberPadded,
      name_project: item?.name ?? '',
      order_id: item?.order_id ?? '',
      request_id: item?.request_id ?? '',
      start_project: item?.start_date ?? '',
      status: item?.status ?? '',
      crm_id: item?.crm_id ?? null,
      crm_sync_state: item?.crm_sync_state ?? null,
      crm_sync_at: item?.crm_sync_at ?? null,
      crm_sync_error: item?.crm_sync_error ?? null
    };
  }

  private mapProjectsResponse(res: any): WebserviceResponse<Projects[]> {
    if (res?.object && Array.isArray(res.object)) {
      return res as WebserviceResponse<Projects[]>;
    }

    const data = Array.isArray(res?.data) ? res.data : [];
    return {
      object: data.map((item: any) => this.mapProjectFromRms(item)),
      token: undefined,
      hasErrors: (res?.status ?? '').toString().toLowerCase() !== 'success',
      message: res?.message ?? '',
      praefix: undefined,
      errors: [],
      firstError: undefined
    };
  }

  // New store for Projects

  public startProject(cid: number):Observable<WebserviceResponse<any>> {
    return this.http.post<WebserviceResponse<any>>(
      `${this.endpoint}/projects/init_project?cid=${cid}`,
        {},
      { responseType: 'json'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public getProjectsPPS(status: string): Observable<WebserviceResponse<Projects[]>> {
    const options = new HttpParams({
      fromObject: {
        cid: 0,
        project_id: '0',
        employee_leader: 0,
        status: status
      }
    });
    return this.http.get<WebserviceResponse<Projects[]>>(
        this.endpoint + '/projects/get_projects',
      { params: options }).pipe(
          retry(3),
          catchError(this.handleError.bind(this))
    );
  }

  public getSingleProject(company: number, projectID: string): Observable<WebserviceResponse<Projects[]>> {
    const options = new HttpParams({
      fromObject: {
        cid: company,
        project_id: projectID
      }
    });
    return this.http.get<WebserviceResponse<Projects[]>>(
        this.endpoint + '/projects/get_single_project',
        { params: options }).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public getUsedProjectsPPS(component: string): Observable<WebserviceResponse<Projects[]>> {
    const options = new HttpParams({
      fromObject: {
        technical_drawing: component
      }
    });
    return this.http.get<WebserviceResponse<Projects[]>>(
        this.endpoint + '/projects/projects_by_component',
      { params: options }).pipe(
          retry(3),
          catchError(this.handleError.bind(this))
    );
  }

  public getUsedInJobs(component: string): Observable<WebserviceResponse<Jobs[]>> {
    const options = new HttpParams({
      fromObject: {
        technical_drawing: component
      }
    });
    return this.http.get<WebserviceResponse<Jobs[]>>(
        this.endpoint + '/jobs/jobs_by_component',
      { params: options }).pipe(
          retry(3),
          catchError(this.handleError.bind(this))
    );
  }

  public getUsedInOrders(component: string): Observable<WebserviceResponse<Orders[]>> {
    const options = new HttpParams({
      fromObject: {
        technical_drawing: component
      }
    });
    return this.http.get<WebserviceResponse<Orders[]>>(
        this.endpoint + '/orders/orders_by_component',
      { params: options }).pipe(
          retry(3),
          catchError(this.handleError.bind(this))
    );
  }

  public getProjectsByUser(userID: number): Observable<WebserviceResponse<Projects[]>> {
    const options = new HttpParams({
      fromObject: {
        user_id: userID,
        status: "all",
      }
    });
    return this.http.get<any>(
        this.endpoint + '/projects/get_projects_by_user_id',
      { params: options }).pipe(
          map(res => this.mapProjectsResponse(res)),
          retry(3),
          catchError(this.handleError.bind(this))
    );
  }

  public filterProjectsPPS(companyID: number, projectID: string, employeeID: number, status: string) : Observable<WebserviceResponse<Projects[]>> {
    const options = new HttpParams({
      fromObject: {
        cid: companyID,
        project_id: projectID,
        employee_leader: employeeID,
        status: status
      }
    });
    return this.http.get<WebserviceResponse<Projects[]>>(
        this.endpoint + '/projects/get_projects',
      { params: options }).pipe(
          retry(3),
          catchError(this.handleError.bind(this))
    );
  }

  public updateProject(s: Projects, cid: number, pid: string): Observable<WebserviceResponse<any>> {
    return this.http.post<WebserviceResponse<any>> (
      `${this.endpoint}/projects/fill_project_data?cid=${cid}&project_id=${pid}`,
      s,
      { responseType: 'json'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  getCrmCompare(projectRef: string, sessionId?: string): Observable<ProjectCrmCompareResponse> {
    let params = new HttpParams();
    if (sessionId && sessionId.trim().length > 0) {
      params = params.set('session_id', sessionId);
    }

    return this.http.get<ProjectCrmCompareResponse>(
      `${this.endpoint}/projects/${encodeURIComponent(projectRef)}/crm_compare`,
      { params }
    ).pipe(
      retry(3),
      catchError(this.handleError.bind(this))
    );
  }

  syncCrmProject(projectRef: string, payload: CrmSyncRequest): Observable<ProjectCrmCompareResponse> {
    return this.http.post<ProjectCrmCompareResponse>(
      `${this.endpoint}/projects/${encodeURIComponent(projectRef)}/crm_sync`,
      payload
    ).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  public updateComment(cid: number, pid: string, comment: ProjectsComment) {
    return this.http.put(
      `${this.endpoint}/projects/update_comment?company_id=${cid}&project_id=${pid}`,
      comment,
      { responseType: 'json'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public deleteProject(cid: number, pid: string): Observable<WebserviceResponse<any>> {
    return this.http.delete<WebserviceResponse<any>>(
      `${this.endpoint}/projects/delete_project?cid=${cid}&project_id=${pid}`
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public getDeleteProjectImpact(cid: number, pid: string): Observable<WebserviceResponse<ProjectDeleteImpact>> {
    const options = new HttpParams({
      fromObject: {
        cid: cid.toString(),
        project_id: pid
      }
    });
    return this.http.get<WebserviceResponse<ProjectDeleteImpact>>(
      `${this.endpoint}/projects/get_delete_project_impact`,
      { params: options }
    ).pipe(
      map((response) => normalizeSingletonResponse(response)),
      retry(3),
      catchError(this.handleError.bind(this))
    );
  }

  // Project Status

  public updateProjectStatus(cid: number, prid: string, status: string){
    return this.http.put(
      `${this.endpoint}/projects/update_status?company_id=${cid}&project_id=${prid}&status=${status}`,
      { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  // New store for Projects positions

  public getPositions(projectID: string, positionID: number, partID: number): Observable<WebserviceResponse<Positions[]>> {
    const options = new HttpParams({
      fromObject: {
        project_number: projectID,
        position: positionID.toString(),
        part: partID.toString()
      }
    });
    return this.http.get<WebserviceResponse<Positions[]>>(
        this.endpoint + '/projects/get_projects_position',
      { params: options }).pipe(
          retry(3),
          catchError(this.handleError.bind(this))
    );
  }

  // test get Components with parent and subs

  public getPositionsByPid(projectID: string): Observable<Positions[]> {
    const options = new HttpParams({
      fromObject: {
        project_number: projectID
      }
    });
    return this.http.get<Positions[]>(
        this.endpoint + '/projects/get_component_by_pid', { params: options }
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public addPosition(p: PositionsList) {
    return this.http.post(
      `${this.endpoint}/projects/init_project_position`,
      p,
      { responseType: 'json'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public addPositionParts(projectID: string, positionID: string, parentID: string, multiplicator: number): Observable<any> {
    const options = new HttpParams({
      fromObject: {
        project_number: projectID,
        position: positionID,
        technical_drawing: parentID,
        count: multiplicator.toString()
      }
    });
    return this.http.get<any>(
        this.endpoint + '/projects/add_sub_components_project_position', { params: options }
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public updatePosition(position: PositionsList): Observable<WebserviceResponse<PositionsList>> {
    return this.http.put<WebserviceResponse<PositionsList>>(
        `${this.endpoint}/projects/update_project_position`,
        position
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public deletePosition(projectID: string, positionID: number, partID: number) {
    return this.http.delete(
      `${this.endpoint}/projects/delete_project_position?project_number=${projectID}&position=${positionID}&position_part=${partID}`,
      { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  // New store for Projects parts

  public getPositionsParts(projectID: string, positionID: string, partNo: string): Observable<WebserviceResponse<Positions>> {
    const options = new HttpParams({
      fromObject: {
        project_number: projectID,
        position: positionID,
        part: partNo
      }
    });
    return this.http.get<WebserviceResponse<Positions>>(
        this.endpoint + '/projects/get_projects_position',
      { params: options }).pipe(
          map((response) => normalizeSingletonResponse(response)),
          retry(3),
          catchError(this.handleError.bind(this))
    );
  }
}
